import React from 'react'
import { Col, Container, Form, Modal as BootstrapModal, Row } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { Controller, useForm } from 'react-hook-form'
import { TemplateCloneRequest, TemplateValue } from '../../../models/TemplateLibrary'
import { TemplateTypeBadge } from './TemplateTypeBadge'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'

export interface TemplateDuplicateModalProps {
  show: boolean
  loading: boolean
  templateValue: TemplateValue
  onDuplicate: (request: TemplateCloneRequest) => void
  onCancel: () => void
}

export const TemplateDuplicateModal: React.FC<TemplateDuplicateModalProps> = ({ show, loading, templateValue, onDuplicate, onCancel }) => {
  const { tracked } = useTracking()
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])

  const { control, getValues } = useForm<TemplateCloneRequest>({
    defaultValues: {
      name: `Copy of ${templateValue.name}`,
      description: templateValue.description,
      scope: 'user',
    } as TemplateCloneRequest,
  })

  const handleDuplicate = tracked(
    onDuplicate,
    {
      eventName: TrackEvent.TEMPLATE_DUPLICATED,
    },
    {
      condition: !!templateValue,
      eventDetailsExtractor: (request) => ({
        ...getTemplateLibraryTrackingDetails(templateValue!),
        targetScope: request.scope,
      }),
    },
  )

  const body = (
    <Container>
      <Row className='m-0'>
        <Col className='p-0'>
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <div>
                <Form.Label>Name</Form.Label>
                <Form.Control id={field.name} placeholder='Enter a name...' {...field} />
              </div>
            )}
          />
        </Col>
      </Row>
      <Row className='m-0 pt-4'>
        <Col className='p-0'>
          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <div>
                <Form.Label>Description</Form.Label>
                <Form.Control id={field.name} as='textarea' rows={3} placeholder='Enter a description...' {...field} />
              </div>
            )}
          />
        </Col>
      </Row>
      <Row className='m-0 pt-4'>
        <Col className='p-0'>
          <Form.Label>Type</Form.Label>
          <div className='d-flex justify-content-start'>
            <TemplateTypeBadge templateValue={templateValue} />
          </div>
        </Col>
      </Row>
      <Row className='m-0 pt-4'>
        <Col className='p-0'>
          {hasTemplateAdminScope ? (
            <Controller
              name='scope'
              control={control}
              render={({ field }) => (
                <div>
                  <Form.Label>Access</Form.Label>
                  <Form.Check inline label={<PersonalAccessItem />} type='radio' id='user' {...field} value='user' checked={field.value === 'user'} />
                  <Form.Check inline label={<AccountAccessItem />} type='radio' id='account' {...field} value='account' checked={field.value === 'account'} />
                </div>
              )}
            />
          ) : (
            <div>
              <Form.Label>Access</Form.Label>
              <PersonalAccessItem />
            </div>
          )}
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      header={<BootstrapModal.Title>Duplicate template</BootstrapModal.Title>}
      body={body}
      show={show}
      onHide={onCancel}
      footerPrimaryActionTitle='Duplicate'
      footerPrimaryActionLoading={loading}
      showFooterActions={true}
      centered
      onFooterPrimaryAction={() => handleDuplicate(getValues())}
      onFooterSecondaryAction={onCancel}
      headerBodySeparator={true}
    />
  )
}

const PersonalAccessItem: React.FC = () => {
  return <AccessItem iconClassName='bi bi-person-fill' label='Personal' description='only you can use' />
}

const AccountAccessItem: React.FC = () => {
  return <AccessItem iconClassName='bi bi-people-fill' label='Account' description='everyone in your organization can use' />
}

interface AccessItemProps {
  iconClassName: string
  label: string
  description: string
}

const AccessItem: React.FC<AccessItemProps> = ({ iconClassName, label, description }) => {
  return (
    <div className='d-flex gap-2 align-items-center'>
      <i className={`${iconClassName} flex-shrink-0`} />
      <div className='flex-shrink-0'>
        {label}
        <span className='zocks-text-gray-600'>{` (${description})`}</span>
      </div>
    </div>
  )
}
